import { db } from '../lib/db';
import { queueAction, processSyncQueue } from '../lib/sync';

// Deixe vazio, o Nginx cuida do roteamento
const API_URL = '';
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

export function setOnlineStatus(online) {
  isOnline = online;
  if (online) processSyncQueue();
}

const getToken = () => localStorage.getItem('token');

async function fetchWithAuth(endpoint, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  // Garante que a rota sempre comece com /api/ para o Nginx interceptar corretamente
  const path = endpoint.startsWith('/api/') ? endpoint : `/api${endpoint}`;

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Sessão expirada');
    }
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Erro na requisição');
  }

  return response.json();
}

function generateTempId() {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// --- Login (sempre online) ---
export const login = async (email, password) => {
  const response = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Erro no login');
  }

  localStorage.setItem('token', data.token);
  return data.user;
};

export const logout = () => {
  localStorage.removeItem('token');
  window.location.href = '/login';
};

export const getDashboardResumo = async () => {
  return fetchWithAuth('/dashboard/resumo');
};

export const getCurrentUser = () => fetchWithAuth('/auth/me');

export const getCelcoinClients = async (params = {}) => {
  const query = new URLSearchParams({
    limit: params.limit || 50,
    startAt: params.startAt ?? 0,
  }).toString();
  return fetchWithAuth(`/celcoin/clientes?${query}`);
};

export const getClients = async (queryString = '') => {
  if (isOnline) {
    try {
      const result = await fetchWithAuth(`/clients?${queryString}`);
      const clientsToCache = result.data || (Array.isArray(result) ? result : []);
      if (clientsToCache.length) {
        await db.clients.bulkPut(clientsToCache);
      }
      return result;
    } catch (err) {
      console.error('Erro online, tentando cache:', err);
    }
  }
  return db.clients.toArray();
};

export const getClient = async (id) => {
  if (isOnline) {
    try {
      const client = await fetchWithAuth(`/clients/${id}`);
      await db.clients.put(client);
      return client;
    } catch (err) {
      console.error('Erro online, tentando cache:', err);
    }
  }
  return db.clients.get(id);
};

export const createClient = async (data) => {
  if (isOnline) {
    const result = await fetchWithAuth('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await db.clients.put(result);
    return result;
  } else {
    const tempId = generateTempId();
    const localClient = { ...data, id: tempId, _pending: true };
    await db.clients.add(localClient);
    await queueAction(`${API_URL}/api/clients`, 'POST', data);
    return localClient;
  }
};

export const updateClient = async (id, data) => {
  if (isOnline) {
    const result = await fetchWithAuth(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    await db.clients.put(result);
    return result;
  } else {
    const existing = await db.clients.get(id);
    const updated = { ...existing, ...data, _pending: true };
    await db.clients.put(updated);
    await queueAction(`${API_URL}/api/clients/${id}`, 'PUT', data);
    return updated;
  }
};

export const deleteClient = async (id) => {
  if (isOnline) {
    await fetchWithAuth(`/clients/${id}`, { method: 'DELETE' });
    await db.clients.delete(id);
  } else {
    await db.clients.delete(id);
    await queueAction(`${API_URL}/api/clients/${id}`, 'DELETE', null);
  }
};

// --- Pagamentos ---
export const getPayments = async (sort = '-created_date', limit = 500) => {
  const query = `sort=${sort}&limit=${limit}`;
  if (isOnline) {
    try {
      const result = await fetchWithAuth(`/payments?${query}`);
      await db.payments.bulkPut(result);
      return result;
    } catch (err) {
      console.error('Erro online, tentando cache:', err);
    }
  }
  return db.payments.toArray();
};

export const getPaymentHistory = async (clienteId) => {
  if (isOnline) {
    try {
      const result = await fetchWithAuth(`/payments/history/${clienteId}`);
      return Array.isArray(result) ? result : [];
    } catch (err) {
      console.error('Erro ao buscar histórico de pagamentos online:', err);
      return db.payments.where('client_id').equals(clienteId).toArray();
    }
  }
  return db.payments.where('client_id').equals(clienteId).toArray();
};

export const getPaymentsList = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return fetchWithAuth(`/payments/list?${query}`);
};

export const getPaymentsSummaryDetailed = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return fetchWithAuth(`/payments/summary-detailed?${query}`);
};

// --- Cobranças (charges) ---
export const getCharges = async (sort = '-created_date', limit = 100) => {
  const query = `sort=${sort}&limit=${limit}`;
  if (isOnline) {
    try {
      const result = await fetchWithAuth(`/charges?${query}`);
      await db.charges.bulkPut(result);
      return result;
    } catch (err) {
      console.error('Erro online, tentando cache:', err);
    }
  }
  return db.charges.toArray();
};

export const createCharge = async (data) => {
  if (isOnline) {
    const result = await fetchWithAuth('/charges', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await db.charges.put(result);
    return result;
  } else {
    const tempId = generateTempId();
    const local = { ...data, id: tempId, _pending: true };
    await db.charges.add(local);
    await queueAction(`${API_URL}/api/charges`, 'POST', data);
    return local;
  }
};

// --- Campanhas ---
export const getCampaigns = async (sort = '-created_date', limit = 100) => {
  const query = `sort=${sort}&limit=${limit}`;
  if (isOnline) {
    try {
      const result = await fetchWithAuth(`/campaigns?${query}`);
      await db.campaigns.bulkPut(result);
      return result;
    } catch (err) {
      console.error('Erro online, tentando cache:', err);
    }
  }
  return db.campaigns.toArray();
};

export const createCampaign = async (data) => {
  if (isOnline) {
    const result = await fetchWithAuth('/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await db.campaigns.put(result);
    return result;
  } else {
    const tempId = generateTempId();
    const local = { ...data, id: tempId, _pending: true };
    await db.campaigns.add(local);
    await queueAction(`${API_URL}/api/campaigns`, 'POST', data);
    return local;
  }
};

// --- Conversas ---
export const getConversations = async (filters = {}, sort = '-updated_date', limit = 100) => {
  let query = `sort=${sort}&limit=${limit}`;
  if (filters.status) query += `&status=${filters.status}`;
  if (isOnline) {
    try {
      const result = await fetchWithAuth(`/conversations?${query}`);
      await db.conversations.bulkPut(result);
      return result;
    } catch (err) {
      console.error('Erro online, tentando cache:', err);
    }
  }
  let collection = db.conversations.toCollection();
  if (filters.status) collection = collection.filter(c => c.status === filters.status);
  const convs = await collection.toArray();
  return convs;
};

export const getConversation = async (id) => {
  if (isOnline) {
    try {
      const conv = await fetchWithAuth(`/conversations/${id}`);
      await db.conversations.put(conv);
      return conv;
    } catch (err) {
      console.error('Erro online, tentando cache:', err);
    }
  }
  return db.conversations.get(id);
};

export const createConversation = async (data) => {
  if (isOnline) {
    const result = await fetchWithAuth('/conversations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await db.conversations.put(result);
    return result;
  } else {
    const tempId = generateTempId();
    const local = { ...data, id: tempId, _pending: true };
    await db.conversations.add(local);
    await queueAction(`${API_URL}/api/conversations`, 'POST', data);
    return local;
  }
};

export const updateConversation = async (id, data) => {
  if (isOnline) {
    const result = await fetchWithAuth(`/conversations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    await db.conversations.put(result);
    return result;
  } else {
    const existing = await db.conversations.get(id);
    const updated = { ...existing, ...data, _pending: true };
    await db.conversations.put(updated);
    await queueAction(`${API_URL}/api/conversations/${id}`, 'PUT', data);
    return updated;
  }
};

// --- Permissões ---
export const getAttendantPermissions = async (sort = '-created_date', limit = 100) => {
  const query = `sort=${sort}&limit=${limit}`;
  if (isOnline) {
    try {
      const result = await fetchWithAuth(`/attendantpermissions?${query}`);
      return result;
    } catch (err) {
      console.error('Erro online, sem cache para permissões:', err);
    }
  }
  return [];
};

export const createAttendantPermission = (data) =>
  fetchWithAuth('/attendantpermissions', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateAttendantPermission = (id, data) =>
  fetchWithAuth(`/attendantpermissions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteAttendantPermission = (id) =>
  fetchWithAuth(`/attendantpermissions/${id}`, { method: 'DELETE' });

// --- Upload ---
export const uploadFile = async (file) => {
  if (!isOnline) throw new Error('Upload requer conexão');
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const response = await fetch(`${API_URL}/api/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  if (!response.ok) throw new Error('Erro no upload');
  return response.json();
};

export const getDistinctClients = () => fetchWithAuth('/clients/distinct');
export const recalcClientStatus = () => fetchWithAuth('/clients/recalc-status', { method: 'POST' });
export const getAllClients = () => fetchWithAuth('/clients/all');
export const syncPaymentHistory = () => fetchWithAuth('/payments/sync-history', { method: 'POST' });

export const updateClientStatusFromExcel = async (file) => {
  if (!isOnline) throw new Error('Upload requer conexão');
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const response = await fetch(`${API_URL}/api/clients/update-status-from-excel`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Erro no upload');
  }
  return response.json();
};

export const getClientsBySegment = async (tipo, situacao = 'todos', countOnly = false) => {
  const query = `tipo=${tipo}&situacao=${situacao}&countOnly=${countOnly}`;
  return fetchWithAuth(`/clients/by-segment?${query}`);
};

export const uploadExcelContacts = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const response = await fetch(`${API_URL}/api/clients/upload-excel-contacts`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) throw new Error('Erro ao processar planilha');
  return response.json();
};

// ========== Cobrança CPFL ==========
export const createCpflCharge = async (formData) => {
  const token = getToken();
  const response = await fetch(`${API_URL}/api/cpfl/cadastrar-cobranca`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.mensagem || 'Erro ao gerar cobrança CPFL');
  }
  return response.json();
};

export const updateAsaasLastInvoices = async () => {
  return fetchWithAuth('/asaas/update-last-invoices', {
    method: 'POST'
  });
};

export const getAsaasStatistics = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return fetchWithAuth(`/asaas/statistics?${query}`);
};

export const apiFetch = fetchWithAuth;
